import React, { useEffect } from 'react';

import { Collapse } from '@mui/material';
import classNames from 'classnames';

import { getThemeClassNames } from '@cognite/cogs-utils-v11/helpers/get-theme-class-names/get-theme-class-names.ts';

import AccordionHeader from './accordion-header';
import type { AccordionProps } from './types';

/**
 * Accordion
 * =============================================================================
 * An accordion is a vertically stacked list of headers that reveal or hide associated sections of content.
 *
 * [Open docs »](https://cognitedata.github.io/cogs.js/storybook/?path=/docs/core_core-accordion--docs)
 *
 * -----------------------------------------------------------------------------
 * Examples
 * -----------------------------------------------------------------------------
 * ```
 * <Accordion
 *  title="Which browsers can I use?"
 *  subtitle="Last updated 6 hours ago"
 * >
 *   You can use the latest versions of all major browsers.
 * </Accordion>
 *
 * ```
 *
 * [Read the full documentation »](https://cognitedata.github.io/cogs.js/storybook/?path=/docs/core_core-accordion--docs)
 */
export const Accordion: React.ForwardRefExoticComponent<
  AccordionProps & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      className,
      children,
      title,
      subtitle,
      size = 'medium',
      type = 'solid',
      inverted,
      disabled = false,
      expanded = false,
      showBorder = false,
      indicatorPlacement = 'right',
      hidePadding = false,
      onChange,
      id = undefined,
      themeProps,
      ...otherProps
    },
    ref
  ) => {
    const [_expanded, setExpanded] = React.useState(expanded);

    useEffect(() => {
      setExpanded(expanded);
    }, [expanded]);

    const expand = () => {
      setExpanded(!_expanded);
      if (onChange) onChange(!_expanded);
    };

    return (
      <div
        {...otherProps}
        className={classNames(
          'cogs-v11',
          'cogs-accordion',
          `cogs-accordion--size-${size}`,
          `cogs-accordion--type-${type}`,
          _expanded ? `cogs-accordion--expanded` : '',
          disabled ? `cogs-accordion--disabled` : '',
          showBorder ? `cogs-accordion--showBorder` : '',
          hidePadding ? `cogs-accordion--hidePadding` : '',
          ...getThemeClassNames(inverted, themeProps),
          className
        )}
        ref={ref}
        aria-disabled={disabled}
        id={id}
      >
        <AccordionHeader
          disabled={disabled}
          expand={expand}
          title={title}
          expanded={_expanded}
          subtitle={subtitle}
          indicatorPlacement={indicatorPlacement}
        />
        <Collapse in={_expanded} timeout="auto" unmountOnExit>
          <div className="cogs-v11 cogs-accordion__content">{children}</div>
        </Collapse>
      </div>
    );
  }
);
